import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import baseService from "../../../services/baseService";
import CarCard from "../../cards/CarCard";

// Shown when a vehicle can no longer be loaded. Auction cars are removed from
// the data feed as soon as they sell, so this is almost always a sold car
// (often reached from an ad or a saved link). Instead of a dead end, show
// live vehicles like it: Meta ads link here with ?make=&model=&year=.
// Same rules as the ads: import age limit (10 years) and cars that run.
const MIN_YEAR = new Date().getFullYear() - 10;
const QUALITY = { year_from: MIN_YEAR, status: ["Run & Drive", "Starts"] };

const build = (params) => {
  const q = new URLSearchParams({ page: 1, size: 10 });
  Object.entries(params).forEach(([k, v]) =>
    (Array.isArray(v) ? v : [v]).forEach((x) => x && q.append(k, x))
  );
  return q.toString().replace(/\+/g, "%20");
};

function ErrorComponent() {
  const { search } = useLocation();
  const { lotID } = useParams();
  const fromLink = useMemo(() => {
    const p = new URLSearchParams(search);
    return {
      make: p.get("make") || "",
      model: p.get("model") || "",
      year: p.get("year") || "",
      buyNow: p.get("buy_now") === "1",
    };
  }, [search]);
  const [hint, setHint] = useState(fromLink);
  const [hintReady, setHintReady] = useState(Boolean(fromLink.make));

  // Older links don't carry the car's details: look up the sold car instead.
  useEffect(() => {
    setHint(fromLink);
    if (fromLink.make || !lotID) {
      setHintReady(true);
      return;
    }
    let cancelled = false;
    setHintReady(false);
    baseService
      .get(`cars/get-sold-car?lot_id=${encodeURIComponent(lotID)}`)
      .then((res) => {
        const c = res?.data?.data;
        if (!cancelled && c?.make)
          setHint({ make: c.make, model: c.model || "", year: c.year ? String(c.year) : "", buyNow: !!c.buy_now });
      })
      .catch(() => {})
      .finally(() => !cancelled && setHintReady(true));
    return () => {
      cancelled = true;
    };
  }, [fromLink, lotID]);

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (!hintReady) return;
    let cancelled = false;
    const scopes = [
      hint.make && hint.model && { params: { make: hint.make, model: hint.model }, label: `${hint.make} ${hint.model}` },
      hint.make && { params: { make: hint.make }, label: hint.make },
      { params: { vehicle_type: "automobile" }, label: "" },
    ].filter(Boolean);
    // A Buy Now car is matched with Buy Now cars first.
    const attempts = scopes.flatMap((sc) => [
      ...(hint.buyNow ? [{ ...sc, params: { ...QUALITY, ...sc.params, buy_now: "true" }, buyNow: true }] : []),
      { ...sc, params: { ...QUALITY, ...sc.params } },
    ]);

    (async () => {
      setLoading(true);
      for (const a of attempts) {
        try {
          const res = await baseService.get(`cars/get-all-cars?${build(a.params)}`);
          const found = res?.data?.data?.cars || [];
          if (found.length) {
            if (!cancelled) {
              setCars(found.slice(0, 8));
              setLabel(a.buyNow ? `${a.label} Buy Now`.trim() : a.label);
            }
            break;
          }
        } catch (e) {
          // try the next, broader search
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [hint.make, hint.model, hint.buyNow, hintReady]);

  const searchLink = useMemo(() => {
    const q = new URLSearchParams();
    if (hint.make) q.append("make", hint.make);
    if (hint.make && hint.model) q.append("model", hint.model);
    q.append("year_from", MIN_YEAR);
    const s = q.toString().replace(/\+/g, "%20");
    return `/search-page${s ? `?${s}` : ""}`;
  }, [hint.make, hint.model]);

  const titleCase = (t) => String(t).toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
  const sold = titleCase([hint.year, hint.make, hint.model].filter(Boolean).join(" "));

  return (
    <div className="flex flex-col w-[92vw] lg:w-[85vw] items-center p-6 lg:p-8 text-center gap-6 font-urbanist">
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">
          {sold ? `This ${sold} has sold` : "This vehicle is no longer available"}
        </h1>
        <p className="text-gray-600 max-w-[640px]">
          Auction vehicles move fast and this one has already gone. Here are similar vehicles you can bid
          on right now — we handle shipping, customs and clearance.
        </p>
      </div>

      <Link
        to={searchLink}
        className="px-5 py-2.5 bg-primary-red text-white rounded-lg shadow-md font-semibold hover:opacity-90"
      >
        {label ? `See all ${titleCase(label)} available now` : "Browse available vehicles"}
      </Link>

      {loading ? (
        <ClipLoader />
      ) : cars.length ? (
        <div className="w-full">
          <h2 className="text-left text-xl lg:text-2xl font-bold mb-3">
            {label ? `Available ${titleCase(label)} vehicles` : "Available now"}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            {cars.map((card) => (
              <CarCard key={card.lot_id} card={card} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ErrorComponent;
